import React from 'react';
import classnames from 'classnames/bind';
import { DateTime } from 'luxon';
import { useParams } from 'react-router-dom';

import { GatewayHardwareMetricsSchema } from 'gateways/types/gateway_hardware_metrics';
import { GatewayParams } from 'types/route_params';

import { useDataGrid } from 'dashboards/hooks/useDataGrid';

import { Heading } from 'components/Heading/Heading';
import { Meter } from 'components/Meter';
import { Text } from 'components/Text/Text';

import styles from './Utilization.module.scss';

const c = classnames.bind(styles);

export function GatewayDetailHardwareUtilization() {
  const { gatewayID } = useParams<GatewayParams>();

  const [timeRange] = React.useState(() => {
    const now = DateTime.now();
    return {
      start: now.minus({ day: 1 }).toISO(),
      stop: now.plus({ day: 1 }).toISO(),
    };
  });

  const { data } = useDataGrid({
    params: {
      dataPool: { name: 'DP_gateway_metrics' },
      // FIXME Only the latest record is needed but timeRange is required param
      timeRange,
      orderByColumn: 'timestamp',
      columns: ['timestamp', 'gateway_metric'],
      sort: 'DESC',
      timeZone: DateTime.now().zoneName,
      filters: [
        {
          column: 'gateway_id',
          operator: 'EQUALS',
          value: gatewayID,
        },
      ],
      first: 10,
    },
  });

  if (!data || data.rows.length === 0) {
    return null;
  }

  const [row] = data.rows;
  const [timestamp] = row;

  let metrics;

  try {
    metrics = GatewayHardwareMetricsSchema.parse(JSON.parse(row[1]));
  } catch {
    //
  }

  if (!metrics) {
    return null;
  }

  const high = 89;
  const max = 100;

  return (
    <div className={c('wrap')}>
      <Text className={c('last-updated')} inline>
        Last updated:{' '}
        {DateTime.fromISO(timestamp).toLocaleString(DateTime.DATETIME_MED)}
      </Text>
      <div className={c('processors')}>
        <section>
          <Heading className={c('heading')} level="3">
            CPU
          </Heading>
          <div className={c('values')}>
            <label className={c('metric')}>
              <Heading level="4" className={c('percent')}>
                {Math.round(metrics.cpu_utilization)}%
              </Heading>
              <Text className={'text'}>Utilization</Text>
              <Meter
                className={c('meter')}
                max={max}
                high={high}
                value={Math.round(metrics.cpu_utilization)}
              />
            </label>
            <label className={c('metric')}>
              <Heading level="4" className={c('percent')}>
                {Math.round(metrics.cpu_memory)}%
              </Heading>
              <Text className={'text'}>Memory</Text>
              <Meter
                className={c('meter')}
                max={max}
                high={high}
                value={Math.round(metrics.cpu_memory)}
              />
            </label>
          </div>
        </section>

        <section>
          <Heading className={c('heading')} level="3">
            GPU
          </Heading>
          <div className={c('values')}>
            <label className={c('metric')}>
              <Heading level="4" className={c('percent')}>
                {Math.round(metrics.gpu_utilization)}%
              </Heading>
              <Text className={'text'}>Utilization</Text>
              <Meter
                className={c('meter')}
                max={max}
                high={high}
                value={Math.round(metrics.gpu_utilization)}
              />
            </label>
            <label className={c('metric')}>
              <Heading level="4" className={c('percent')}>
                {Math.round(metrics.gpu_memory)}%
              </Heading>
              <Text className={'text'}>Memory</Text>
              <Meter
                className={c('meter')}
                max={max}
                high={high}
                value={Math.round(metrics.gpu_memory)}
              />
            </label>
          </div>
        </section>
      </div>

      <div className={c('storage')}>
        <section>
          <Heading className={c('heading')} level="3">
            Storage
          </Heading>
          <label className={c('metric')}>
            <Heading level="4" className={c('percent')}>
              {Math.round(metrics.disk_used_pct)}%
            </Heading>
            <Text className={'text'}>Utilization</Text>
            <Meter
              className={c('storage-meter')}
              max={max}
              high={high}
              size="large"
              value={Math.round(metrics.disk_used_pct)}
            />
          </label>
        </section>
      </div>
    </div>
  );
}
